import { DragEvent, FunctionComponent, KeyboardEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
	faChevronDown,
	faChevronRight,
	faFolder,
	faFolderOpen,
	faFolderPlus,
	faFolderTree,
	faPen,
	faTrash,
} from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";
import { MediaFolder } from "@/pages/Private/redux/media/media.schema";
import { classNames } from "@/utils/classNames";

/**
 * Where the library is being looked at: the root, or one folder. The root is a folder in
 * every way but one — it has no row — which is why it is a word and not a number.
 */
export type FolderSelection = "root" | number;

/** The library entry a card puts on the drag, for a folder row to read on drop. */
export interface LibraryDragItem {
	kind: "generated" | "uploaded";
	id: number;
}

/** Custom drag types, so a folder row ignores anything that is not the library's own. */
export const LIBRARY_ITEM_DRAG_TYPE = "application/x-media-library-item";
const FOLDER_DRAG_TYPE = "application/x-media-library-folder";

/** The folder id the selection means to the server — `0` is the root. */
export const folderIdOf = (selection: FolderSelection): number =>
	selection === "root" ? 0 : selection;

/** The folder and every folder above it, root first — what a breadcrumb reads out. */
export const pathTo = (folders: MediaFolder[], id: number): MediaFolder[] => {
	for (const folder of folders) {
		if (folder.id === id) {
			return [folder];
		}

		const below = pathTo(folder.children, id);

		if (below.length) {
			return [folder, ...below];
		}
	}

	return [];
};

export const findFolder = (folders: MediaFolder[], id: number): MediaFolder | undefined =>
	pathTo(folders, id).pop();

/** The folders directly inside the selection — what the grid shows before the files. */
export const childrenOf = (folders: MediaFolder[], selection: FolderSelection): MediaFolder[] =>
	selection === "root" ? folders : (findFolder(folders, selection)?.children ?? []);

interface MediaFolderTreeProps {
	folders: MediaFolder[];
	selected: FolderSelection;
	onSelect: (selection: FolderSelection) => void;
	/**
	 * Without these the tree is read-only: the picker browses folders, it does not organise
	 * them. With them, rows can be renamed, deleted and dragged, and files dropped on them.
	 */
	onCreate?: (name: string, parentId: number | null) => Promise<void>;
	onRename?: (id: number, name: string) => Promise<void>;
	onMoveFolder?: (id: number, parentId: number | null) => Promise<void>;
	onDelete?: (folder: MediaFolder) => void;
	onDropItem?: (folderId: number | null, item: LibraryDragItem) => Promise<void>;
}

/**
 * The library's folders as a sidebar tree.
 *
 * A file lives in at most one folder, and the tree is only ever as deep as somebody made
 * it — so this is a plain nested list with a chevron per folder that has children. The
 * row's number is the files filed directly in it. "All files" at the top is the root: the
 * folder every other folder hangs from, and where the files nobody has filed sit.
 *
 * Moving is dragging: a card onto a folder files it, onto the root unfiles it; a folder
 * onto another folder nests it, onto the root puts it back at the top. Native HTML drag
 * and drop with the library's own MIME types, so nothing else that can be dragged around
 * a page is mistaken for a file.
 */
export const MediaFolderTree: FunctionComponent<MediaFolderTreeProps> = ({
	folders,
	selected,
	onSelect,
	onCreate,
	onRename,
	onMoveFolder,
	onDelete,
	onDropItem,
}) => {
	const { t } = useTranslation();
	const ts = (key: string, options?: Record<string, unknown>) =>
		t(`courses.mediaLibrary.folders.${key}`, options ?? {});

	const editable = !!onCreate;
	const [expanded, setExpanded] = useState<Set<number>>(() => new Set());
	// The folder a new one is being typed under — `null` for the root, `undefined` when
	// nobody is typing.
	const [creatingUnder, setCreatingUnder] = useState<number | null | undefined>(undefined);
	const [renaming, setRenaming] = useState<number | null>(null);
	// The row a drag is currently over, for the highlight.
	const [over, setOver] = useState<FolderSelection | null>(null);

	// A selected folder is always visible: its ancestors open when it is chosen from
	// somewhere else — the breadcrumb, or a fresh create.
	useEffect(() => {
		if (typeof selected !== "number") {
			return;
		}

		const ancestors = pathTo(folders, selected).slice(0, -1);

		if (ancestors.some((folder) => !expanded.has(folder.id))) {
			setExpanded((current) => new Set([...current, ...ancestors.map((folder) => folder.id)]));
		}
	}, [selected, folders, expanded]);

	const toggle = (id: number) =>
		setExpanded((current) => {
			const next = new Set(current);

			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}

			return next;
		});

	const startCreating = () => {
		const parentId = typeof selected === "number" ? selected : null;

		if (parentId) {
			setExpanded((current) => new Set([...current, parentId]));
		}

		setCreatingUnder(parentId);
	};

	// ── drag and drop ────────────────────────────────────────────────────────────

	const dropProps = (target: FolderSelection) =>
		dropHandlers(target, { onDropItem, onMoveFolder }, setOver);

	const rowClass = (target: FolderSelection) =>
		classNames(
			"group flex items-center gap-1 rounded-card px-2 py-1.5 text-sm",
			selected === target ? "bg-accent-tint font-semibold text-ink" : "text-text hover:bg-surface",
			over === target && "ring-2 ring-accent"
		);

	// ── rows ─────────────────────────────────────────────────────────────────────

	const renderFolder = (folder: MediaFolder, depth: number) => {
		const hasChildren = folder.children.length > 0;
		const isOpen = expanded.has(folder.id);
		const isSelected = selected === folder.id;

		return (
			<li key={folder.id}>
				<div
					className={rowClass(folder.id)}
					draggable={!!onMoveFolder && renaming !== folder.id}
					style={{ paddingLeft: `${8 + depth * 16}px` }}
					onDragStart={(event) => {
						event.dataTransfer.setData(FOLDER_DRAG_TYPE, String(folder.id));
						event.dataTransfer.effectAllowed = "move";
					}}
					{...dropProps(folder.id)}
				>
					{hasChildren ? (
						<button
							aria-label={ts(isOpen ? "collapse" : "expand")}
							className="flex h-5 w-5 shrink-0 items-center justify-center text-subtle"
							type="button"
							onClick={() => toggle(folder.id)}
						>
							<Icon className="h-3 w-3" icon={isOpen ? faChevronDown : faChevronRight} />
						</button>
					) : (
						<span className="h-5 w-5 shrink-0" />
					)}

					<Icon
						className="h-4 w-4 shrink-0 text-subtle"
						icon={isSelected || isOpen ? faFolderOpen : faFolder}
					/>

					{renaming === folder.id ? (
						<NameInput
							initial={folder.name}
							onCancel={() => setRenaming(null)}
							onSubmit={async (name) => {
								setRenaming(null);

								if (name !== folder.name) {
									await onRename?.(folder.id, name);
								}
							}}
						/>
					) : (
						<button
							aria-current={isSelected ? "true" : undefined}
							className="min-w-0 flex-1 truncate text-left"
							title={folder.name}
							type="button"
							onClick={() => onSelect(folder.id)}
							onDoubleClick={() => editable && setRenaming(folder.id)}
						>
							{folder.name}
						</button>
					)}

					{renaming !== folder.id && (
						<>
							<span className="shrink-0 text-xs text-subtle">{folder.itemCount}</span>
							{editable && (
								<span
									className={classNames(
										"flex shrink-0 items-center gap-0.5",
										isSelected ? "" : "opacity-0 group-hover:opacity-100"
									)}
								>
									<button
										aria-label={ts("rename")}
										className="flex h-5 w-5 items-center justify-center rounded text-subtle hover:text-ink"
										title={ts("rename")}
										type="button"
										onClick={() => setRenaming(folder.id)}
									>
										<Icon className="h-3 w-3" icon={faPen} />
									</button>
									<button
										aria-label={ts("delete")}
										className="flex h-5 w-5 items-center justify-center rounded text-subtle hover:text-danger"
										title={ts("delete")}
										type="button"
										onClick={() => onDelete?.(folder)}
									>
										<Icon className="h-3 w-3" icon={faTrash} />
									</button>
								</span>
							)}
						</>
					)}
				</div>

				{(isOpen || creatingUnder === folder.id) && (
					<ul>
						{creatingUnder === folder.id && renderCreateRow(folder.id, depth + 1)}
						{isOpen && folder.children.map((child) => renderFolder(child, depth + 1))}
					</ul>
				)}
			</li>
		);
	};

	const renderCreateRow = (parentId: number | null, depth: number) => (
		<li
			key="new"
			className="flex items-center gap-1 px-2 py-1"
			style={{ paddingLeft: `${8 + depth * 16}px` }}
		>
			<span className="h-5 w-5 shrink-0" />
			<Icon className="h-4 w-4 shrink-0 text-subtle" icon={faFolder} />
			<NameInput
				initial=""
				placeholder={ts("namePlaceholder")}
				onCancel={() => setCreatingUnder(undefined)}
				onSubmit={async (name) => {
					setCreatingUnder(undefined);
					await onCreate?.(name, parentId);
				}}
			/>
		</li>
	);

	return (
		<nav aria-label={ts("title")} className="space-y-1">
			<div className="flex items-center justify-between gap-2 px-2">
				<span className="text-xs font-semibold uppercase text-subtle">{ts("title")}</span>
				{editable && (
					<button
						className="flex items-center gap-1 rounded-full border border-border-soft bg-white px-2 py-1 text-xs font-semibold text-subtle hover:border-accent hover:text-ink"
						type="button"
						onClick={startCreating}
					>
						<Icon className="h-3 w-3" icon={faFolderPlus} />
						{ts("newFolder")}
					</button>
				)}
			</div>

			<ul className="space-y-0.5">
				<li>
					<div className={rowClass("root")} {...dropProps("root")}>
						<span className="h-5 w-5 shrink-0" />
						<Icon className="h-4 w-4 shrink-0 text-subtle" icon={faFolderTree} />
						<button
							aria-current={selected === "root" ? "true" : undefined}
							className="min-w-0 flex-1 truncate text-left"
							type="button"
							onClick={() => onSelect("root")}
						>
							{ts("allFiles")}
						</button>
					</div>
				</li>
				{creatingUnder === null && renderCreateRow(null, 0)}
				{folders.map((folder) => renderFolder(folder, 0))}
			</ul>

			{editable && <p className="px-2 pt-2 text-xs text-subtle">{ts("dropHint")}</p>}
		</nav>
	);
};

interface DropTargets {
	onDropItem?: (folderId: number | null, item: LibraryDragItem) => Promise<void>;
	onMoveFolder?: (id: number, parentId: number | null) => Promise<void>;
}

/**
 * What a folder — a row in the tree or a tile in the grid — does with a drag. A file lands
 * in it, or at the root if the target is the root; a folder nests in it, or returns to the
 * top. Only the library's own types are accepted, and only where a handler exists.
 */
const dropHandlers = (
	target: FolderSelection,
	{ onDropItem, onMoveFolder }: DropTargets,
	setOver: (update: (current: FolderSelection | null) => FolderSelection | null) => void
) => {
	const accepts = (event: DragEvent) => {
		const types = Array.from(event.dataTransfer.types);

		return (
			(types.includes(LIBRARY_ITEM_DRAG_TYPE) && !!onDropItem) ||
			(types.includes(FOLDER_DRAG_TYPE) && !!onMoveFolder)
		);
	};

	return {
		onDragOver: (event: DragEvent) => {
			if (accepts(event)) {
				event.preventDefault();
				event.dataTransfer.dropEffect = "move";
				setOver(() => target);
			}
		},
		onDragLeave: () => setOver((current) => (current === target ? null : current)),
		onDrop: (event: DragEvent) => {
			setOver(() => null);

			if (!accepts(event)) {
				return;
			}

			event.preventDefault();

			const folderId = typeof target === "number" ? target : null;
			const item = event.dataTransfer.getData(LIBRARY_ITEM_DRAG_TYPE);

			if (item) {
				void onDropItem?.(folderId, JSON.parse(item) as LibraryDragItem);

				return;
			}

			const movingId = Number(event.dataTransfer.getData(FOLDER_DRAG_TYPE));

			// Onto itself is a no-op; into its own subtree the server refuses and says why.
			if (movingId && movingId !== folderId) {
				void onMoveFolder?.(movingId, folderId);
			}
		},
	};
};

interface MediaFolderCardProps extends DropTargets {
	folder: MediaFolder;
	onOpen: (id: number) => void;
	/** Lets the tile itself be dragged onto another folder or the root. */
	draggable?: boolean;
}

/**
 * A folder as a tile in the grid, ahead of the files — what makes the root, and every
 * folder, read like a place you are standing in rather than a filter you switched on.
 * Opening it is a click; filing into it is a drop, the same as on its row in the tree.
 */
export const MediaFolderCard: FunctionComponent<MediaFolderCardProps> = ({
	folder,
	onOpen,
	draggable = false,
	onDropItem,
	onMoveFolder,
}) => {
	const { t } = useTranslation();
	const ts = (key: string, options?: Record<string, unknown>) =>
		t(`courses.mediaLibrary.folders.${key}`, options ?? {});
	const [over, setOver] = useState<FolderSelection | null>(null);

	return (
		<div
			className={classNames(
				"flex items-center gap-3 rounded-card border border-border-soft bg-white p-3",
				draggable && "cursor-grab active:cursor-grabbing",
				over === folder.id && "ring-2 ring-accent"
			)}
			draggable={draggable}
			onDragStart={(event) => {
				event.dataTransfer.setData(FOLDER_DRAG_TYPE, String(folder.id));
				event.dataTransfer.effectAllowed = "move";
			}}
			{...dropHandlers(folder.id, { onDropItem, onMoveFolder }, setOver)}
		>
			<button
				aria-label={ts("open", { name: folder.name })}
				className="flex min-w-0 flex-1 items-center gap-3 text-left"
				type="button"
				onClick={() => onOpen(folder.id)}
			>
				<span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-card bg-surface text-subtle">
					<Icon className="h-6 w-6" icon={faFolder} />
				</span>
				<span className="min-w-0">
					<span className="block truncate text-sm font-semibold text-ink" title={folder.name}>
						{folder.name}
					</span>
					<span className="block truncate text-xs text-subtle">
						{ts("files", { count: folder.itemCount })}
						{folder.children.length > 0 &&
							` · ${ts("subfolders", { count: folder.children.length })}`}
					</span>
				</span>
			</button>
		</div>
	);
};

interface NameInputProps {
	initial: string;
	placeholder?: string;
	onSubmit: (name: string) => Promise<void>;
	onCancel: () => void;
}

/** One line of text, committed with Enter or by leaving it, abandoned with Escape. */
const NameInput: FunctionComponent<NameInputProps> = ({
	initial,
	placeholder,
	onSubmit,
	onCancel,
}) => {
	const [value, setValue] = useState(initial);

	const commit = () => {
		const name = value.trim();

		if (!name) {
			onCancel();

			return;
		}

		void onSubmit(name);
	};

	const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Enter") {
			event.preventDefault();
			commit();
		} else if (event.key === "Escape") {
			event.preventDefault();
			onCancel();
		}
	};

	return (
		<input
			autoFocus
			className="min-w-0 flex-1 rounded border border-border bg-white px-1.5 py-0.5 text-sm text-text outline-none focus:border-accent"
			maxLength={128}
			placeholder={placeholder}
			value={value}
			onBlur={commit}
			onChange={(event) => setValue(event.target.value)}
			onKeyDown={onKeyDown}
		/>
	);
};

interface FolderBreadcrumbProps {
	folders: MediaFolder[];
	selected: FolderSelection;
	onSelect: (selection: FolderSelection) => void;
	/** Shown only where it means something: a folder — or the root — that has subfolders. */
	includeSubfolders?: boolean;
	onIncludeSubfolders?: (include: boolean) => void;
}

/**
 * Where the grid is, read from the top: "All files › Trailers › 2026". Every crumb but the
 * last is a way back up. The pill beside it widens a folder to its subtree, which is the
 * one thing a folder view needs that the tree alone cannot say.
 */
export const FolderBreadcrumb: FunctionComponent<FolderBreadcrumbProps> = ({
	folders,
	selected,
	onSelect,
	includeSubfolders = false,
	onIncludeSubfolders,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`courses.mediaLibrary.folders.${key}`);

	const path = typeof selected === "number" ? pathTo(folders, selected) : [];
	const crumbs: { label: string; selection: FolderSelection }[] = [
		{ label: ts("allFiles"), selection: "root" },
		...path.map((folder) => ({ label: folder.name, selection: folder.id })),
	];
	const canWiden = !!onIncludeSubfolders && childrenOf(folders, selected).length > 0;

	return (
		<div className="flex flex-wrap items-center justify-between gap-2">
			<ol className="flex flex-wrap items-center gap-1 text-sm">
				{crumbs.map((crumb, index) => {
					const isLast = index === crumbs.length - 1;

					return (
						<li key={`${crumb.selection}`} className="flex items-center gap-1">
							{index > 0 && <Icon className="h-3 w-3 text-subtle" icon={faChevronRight} />}
							{isLast ? (
								<span aria-current="page" className="font-semibold text-ink">
									{crumb.label}
								</span>
							) : (
								<button
									className="text-subtle hover:text-ink hover:underline"
									type="button"
									onClick={() => onSelect(crumb.selection)}
								>
									{crumb.label}
								</button>
							)}
						</li>
					);
				})}
			</ol>

			{canWiden && (
				<button
					aria-pressed={includeSubfolders}
					className={classNames(
						"rounded-full border px-3 py-1.5 text-xs font-semibold",
						includeSubfolders
							? "border-accent bg-accent-tint text-ink"
							: "border-border-soft bg-white text-subtle"
					)}
					type="button"
					onClick={() => onIncludeSubfolders?.(!includeSubfolders)}
				>
					{ts("includeSubfolders")}
				</button>
			)}
		</div>
	);
};
